import type { CityConfig } from "@/pages/city-template/types";

export const sheboyganConfig: CityConfig = {
  city: "Sheboygan",
  state: "Wisconsin",
  stateAbbr: "WI",
  slug: "sheboygan",
  route: "/sheboygan",
  title: "Emergency & Expedited Passport Services Sheboygan WI | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Sheboygan, WI — as fast as 24 hours. No Wisconsin passport agency. Fast Passport Center couriers to Chicago, about 110 miles south. A+ BBB rated. Serving Sheboygan County, Kohler, Plymouth, and the Lake Michigan lakeshore corridor.",
  h1: "Emergency & Expedited Passport Services in Sheboygan, WI",
  heroSubtitle: "Sheboygan sits on the scenic Lake Michigan shoreline, about 110 miles from the Chicago Passport Agency. Fast Passport Center's registered couriers deliver your documents to Chicago so you get a new or renewed passport as fast as 24 hours — whether you're a Kohler executive heading overseas, a lakeshore family planning a cruise, or a Sheboygan County resident with urgent travel needs.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Sheboygan%20Wisconsin%20Lake%20Michigan%20shoreline%20with%20sandy%20beach%20and%20marina%20harbor%20at%20golden%20hour%2C%20sailboats%20docked%20at%20harbor%2C%20historic%20lighthouse%20on%20pier%2C%20warm%20amber%20tones%20reflecting%20on%20water%2C%20professional%20coastal%20photography%2C%20wide%20angle%20composition%20showing%20lakefront%20and%20downtown%20skyline%2C%20soft%20natural%20light%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=sheboygan-hero-001&orientation=landscape",
  heroImageAlt: "Sheboygan Wisconsin Lake Michigan shoreline and marina harbor at golden hour",
  geoPosition: "43.7508;-87.7145",
  geoPlacename: "Sheboygan, Wisconsin",
  lastModified: "2026-06-05",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Approximately 110 miles from Sheboygan — about 2 hours via I-43 south",
  },
  nearbyCitiesZips: [
    { name: "Downtown Sheboygan", zips: "53081" },
    { name: "South Sheboygan", zips: "53081" },
    { name: "Kohler", zips: "53044" },
    { name: "Plymouth", zips: "53073" },
    { name: "Sheboygan Falls", zips: "53085" },
    { name: "Elkhart Lake", zips: "53020" },
    { name: "Howards Grove", zips: "53083" },
    { name: "Oostburg", zips: "53070" },
    { name: "Cedar Grove", zips: "53013" },
    { name: "Random Lake", zips: "53075" },
    { name: "Manitowoc", zips: "54220" },
    { name: "Kiel", zips: "53042" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Chicago Passport Agency — about 110 miles on the I-43 corridor" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Lakeshore Hub", desc: "Serving Sheboygan, Kohler, Plymouth, and the Lake Michigan corridor" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Sheboygan case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Milwaukee WI", route: "/milwaukee" },
    { name: "Expedited passport in Green Bay WI", route: "/green-bay" },
    { name: "Urgent passport in Appleton WI", route: "/appleton" },
    { name: "Wisconsin passport hub", route: "/wisconsin" },
    { name: "Chicago passport services", route: "/illinois" },
    { name: "Emergency passport in Manitowoc WI", route: "/manitowoc" },
    { name: "Expedited passport in Fond du Lac WI", route: "/fond-du-lac" },
  ],
  fpcOfficeAddress: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
  fpcOfficeNote: "Fast Passport Center serves Sheboygan remotely via secure courier. Our registered couriers hand-deliver your documents to the Chicago Passport Agency. Sheboygan sits on the I-43 lakeshore corridor — about 110 miles from Chicago — providing reliable courier access for all of Sheboygan County.",
  fpcOfficeDistance: "Serving all of Sheboygan County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Sheboygan, Kohler, Plymouth, and the Lake Michigan lakeshore corridor. From the Sheboygan harbor and marina to the world-famous Kohler hospitality community — we help Sheboygan County travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Sheboygan and nearby communities including Kohler, Plymouth, Sheboygan Falls, Elkhart Lake, Howards Grove, Oostburg, and the entire Sheboygan County lakeshore region.",
  howItWorksIntro: "Most Sheboygan travelers complete everything online and ship documents overnight. At about 110 miles from Chicago on the I-43 corridor, Sheboygan enjoys reliable courier turnaround — ideal for Kohler business travelers and Sheboygan County families alike.",
  mistakesIntro: "Sheboygan travelers, including Kohler executives traveling internationally and lakeshore families, have unique passport needs. Here are the most common mistakes we see.",
  testimonialsIntro: "Sheboygan and Sheboygan County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Sheboygan? Here are the most common ones we hear from Sheboygan County travelers.",
  statePageRoute: "/states/wisconsin",
  statePageName: "Wisconsin",
  dmvWebsite: "https://www.dot.wisconsin.gov/drivers",
  dmvLabel: "Wisconsin DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "I-43 corridor access to Chicago",
        "No drive or appointment needed",
        "Ideal for Kohler international business travelers",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from MKE",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "About 110 miles to Chicago (2 hours via I-43)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Sheboygan is about 110 miles from the Chicago Passport Agency via I-43. Our couriers achieve same-day delivery for 24-hour turnaround.",
      bullets: ["I-43 lakeshore corridor to Chicago", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in WI",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Sheboygan adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Sheboygan and Kohler"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Sheboygan and Sheboygan County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Sheboygan, Kohler, and Sheboygan County residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Chicago Agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Sheboygan County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Sheboygan and Kohler residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Sheboygan WI", description: "Urgent passport processing for Sheboygan, WI travelers via Chicago Passport Agency courier — about 110 miles from Sheboygan on the I-43 lakeshore corridor." },
    { name: "Passport Renewal Sheboygan WI", description: "Fast passport renewal for Sheboygan and Sheboygan County, Wisconsin residents." },
    { name: "New Passport Sheboygan WI", description: "First-time passport application assistance for Sheboygan and lakeshore Wisconsin residents." },
    { name: "Lost Passport Sheboygan WI", description: "Passport replacement for lost or stolen passports for Sheboygan and Kohler residents." },
    { name: "Child Passport Sheboygan WI", description: "Minor passport services for Sheboygan County families." },
    { name: "Name Change Passport Sheboygan WI", description: "Passport name change service for Sheboygan, WI residents." },
  ],
  testimonials: [
    {
      name: "William H.",
      location: "Kohler, WI",
      rating: 5,
      text: "Kohler executive heading to a hospitality conference in Dubai. My passport had 4 months of validity — not enough for UAE entry requirements. Fast Passport Center got my renewal done in 4 business days. Essential for international business travel.",
      service: "Expedited Renewal",
    },
    {
      name: "Nancy P.",
      location: "Downtown Sheboygan",
      rating: 5,
      text: "Family cruise departing from Miami — needed passports for all five of us including two kids under 10. Fast Passport Center guided us through the whole process and had everything back in 7 business days.",
      service: "Family Passports",
    },
    {
      name: "Greg T.",
      location: "Plymouth, WI",
      rating: 5,
      text: "Got a last-minute opportunity to join a Road America racing event in Germany. No passport at all. Fast Passport Center helped me do my first-ever application at the Sheboygan post office and rushed everything to Chicago. Received in 5 days.",
      service: "New Passport",
    },
    {
      name: "Susan B.",
      location: "Sheboygan Falls, WI",
      rating: 5,
      text: "My wallet was stolen with my passport card inside — I didn't even realize you needed to report a lost passport card the same way. Fast Passport Center handled the DS-64 for me and got a replacement in 3 business days.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "sheb-1",
      name: "Sheboygan Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "522 N 9th St",
      city: "Sheboygan",
      state: "WI",
      zip: "53081",
      phone: "(920) 459-2250",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "Downtown Sheboygan",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Sheboygan. Appointment required. Call ahead for passport service hours.",
      icon: "ri-mail-line",
    },
    {
      id: "sheb-2",
      name: "Sheboygan County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "615 N 6th St, Room 104",
      city: "Sheboygan",
      state: "WI",
      zip: "53081",
      phone: "(920) 459-3003",
      hours: "Mon–Fri: 7:30 AM – 4:30 PM",
      distance: "Downtown Sheboygan",
      website: "https://www.sheboygancounty.com/departments/county-clerk",
      websiteLabel: "Sheboygan County Clerk",
      notes: "Accepts passport applications for Sheboygan County residents. Located in the Sheboygan County Courthouse.",
      icon: "ri-government-line",
    },
    {
      id: "sheb-3",
      name: "Kohler Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "419 Highland Dr",
      city: "Kohler",
      state: "WI",
      zip: "53044",
      phone: "(920) 457-3491",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "4 miles west of downtown Sheboygan",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Kohler village residents. Limited hours — call ahead to verify passport services.",
      icon: "ri-mail-line",
    },
    {
      id: "sheb-4",
      name: "Plymouth Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "111 E Main St",
      city: "Plymouth",
      state: "WI",
      zip: "53073",
      phone: "(920) 893-4001",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "15 miles west of Sheboygan",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Plymouth and western Sheboygan County residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "sheb-5",
      name: "Elkhart Lake Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "64 E Rhine St",
      city: "Elkhart Lake",
      state: "WI",
      zip: "53020",
      phone: "(920) 876-2810",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "17 miles northwest of Sheboygan",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Elkhart Lake and Road America travelers. Limited hours.",
      icon: "ri-mail-line",
    },
    {
      id: "sheb-6",
      name: "Manitowoc Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "606 Quay St",
      city: "Manitowoc",
      state: "WI",
      zip: "54220",
      phone: "(920) 684-6221",
      hours: "Mon–Fri: 8:30 AM – 5:30 PM",
      distance: "28 miles north of Sheboygan",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northern Sheboygan County and Manitowoc County border residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Chicago Agency", agency: "110 miles — 2-hour drive via I-43, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Assuming Kohler Has Its Own Passport Resources",
      description: "Kohler is a world-class hospitality destination, but it doesn't have a passport agency. The Kohler Post Office offers acceptance services only. For fast processing, Sheboygan County residents need courier service to Chicago — about 110 miles away.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Background for International Business",
      description: "Kohler executives frequently travel internationally and need photos that meet strict specifications. Passport photos must be 2x2 inches with a pure white background and taken within 6 months. Even slight color variations cause rejection.",
    },
    {
      icon: "ri-file-list-line",
      title: "Confusing Passport Card with Passport Book",
      description: "Many Sheboygan County residents have a passport card for Canada travel but need a passport book for international flights. A passport card cannot be used for air travel — you need the full passport book.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Booking International Travel to Elkhart Lake Events",
      description: "Road America and Elkhart Lake events attract international visitors, and many Sheboygan residents reciprocate by attending overseas racing events. Don't book international race tickets without a valid passport in hand.",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passports Without Both Parents",
      description: "Minors under 16 require both parents at the acceptance facility or a notarized DS-3053. Sheboygan families sometimes overlook this when one parent is traveling for Kohler business.",
    },
    {
      icon: "ri-time-line",
      title: "Not Checking Passport Expiration Before Cruise Season",
      description: "Sheboygan's lakeshore location makes cruises popular — but many families book cruises without checking passport validity. Always verify expiration dates before booking, especially for the busy summer cruise season.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Sheboygan, Kohler, or Plymouth." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to Chicago — about 110 miles via I-43." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Chicago Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Sheboygan County address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Sheboygan from the Chicago Passport Agency?",
      a: "Sheboygan is approximately 110 miles from the Chicago Passport Agency — about 2 hours via I-43 south. While not as close as Racine or Kenosha, Sheboygan's lakeshore corridor provides reliable access for courier delivery to Chicago.",
    },
    {
      q: "Do you serve Kohler residents?",
      a: "Yes. We serve Kohler village residents and Kohler Company executives who travel internationally. Kohler is a world-class hospitality brand, and we understand the urgency of business travel. Expedited processing is 2–9 business days, emergency processing is 24–48 hours.",
    },
    {
      q: "How fast can I get an emergency passport in Sheboygan?",
      a: "Emergency processing is typically 24–48 hours. Sheboygan is about 110 miles from Chicago — our couriers make the trip via I-43 and can achieve same-day delivery for urgent cases.",
    },
    {
      q: "Where do Sheboygan travelers fly internationally?",
      a: "Sheboygan travelers typically fly internationally through Milwaukee Mitchell (MKE, 60 miles south), Green Bay Austin Straubel (GRB, 65 miles north), or Chicago O'Hare (ORD, 130 miles south). MKE offers the most convenient international connections from Sheboygan.",
    },
    {
      q: "What if I need a passport for a Road America international event?",
      a: "Road America in Elkhart Lake attracts international racing events, and many Sheboygan County residents attend overseas races. Fast Passport Center serves the motorsports community with expedited passport services. We recommend starting at least 6 weeks before international race travel.",
    },
    {
      q: "How do I renew my passport in Sheboygan?",
      a: "Adult renewals (passport issued at 16 or older, within the past 15 years, in your current name) can be done entirely by mail with Form DS-82. Fast Passport Center achieves 2–9 business day turnaround — far faster than standard USPS processing (8–11 weeks).",
    },
    {
      q: "What documents do I need for a first-time passport in Sheboygan?",
      a: "You'll need Form DS-11, a certified Wisconsin birth certificate, a valid photo ID, two passport photos, and fee payment. First-time applicants must appear in person at an acceptance facility like the Sheboygan Main Post Office or Sheboygan County Clerk.",
    },
    {
      q: "Does the Sheboygan post office take walk-ins for passport applications?",
      a: "The Sheboygan Main Post Office on N 9th St generally requires appointments for passport services. Walk-in availability varies. Call ahead or use the USPS online scheduler to secure your appointment.",
    },
    {
      q: "Can you serve Plymouth and Sheboygan Falls residents?",
      a: "Yes. We serve all of Sheboygan County including Plymouth, Sheboygan Falls, Elkhart Lake, Howards Grove, Oostburg, Cedar Grove, Random Lake, and beyond. Documents ship overnight from any UPS location in Sheboygan County.",
    },
    {
      q: "What's the difference between a passport book and passport card for Sheboygan residents?",
      a: "A passport card is only valid for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean. A passport book is required for all international air travel. Sheboygan County residents who travel internationally by air always need a passport book — the card won't work at the airport.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Sheboygan", address: "2909 S Business Dr, Sheboygan, WI 53081", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Sheboygan", address: "1405 N Taylor Dr, Sheboygan, WI 53081", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Plymouth", address: "2626 Eastern Ave, Plymouth, WI 53073", hours: "Mon–Sun: 7AM–10PM" },
    { name: "FedEx Office — Sheboygan", address: "3315 S Business Dr, Sheboygan, WI 53081", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–5PM" },
    { name: "UPS Store — Sheboygan", address: "2805 S Business Dr, Sheboygan, WI 53081", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
    { name: "Walgreens — Sheboygan Falls", address: "1136 Fond du Lac Ave, Sheboygan Falls, WI 53085", hours: "Mon–Sun: 7AM–10PM" },
  ],
  dmvLocations: [
    { name: "WI DMV — Sheboygan", address: "3709 Kohler Memorial Dr, Sheboygan, WI 53081", hours: "Mon–Fri: 8:30AM–4:30PM", note: "REAL ID and driver's license renewals for Sheboygan County residents.", featured: true },
    { name: "WI DMV — Manitowoc", address: "2107 Mirro Dr, Manitowoc, WI 54220", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves Manitowoc County and northern Sheboygan County residents.", featured: false },
    { name: "WI DMV — West Bend", address: "903 E Paradise Dr, West Bend, WI 53095", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves Washington County and western Sheboygan County residents.", featured: false },
  ],
  airports: [
    {
      code: "MKE",
      name: "Milwaukee Mitchell International Airport",
      distance: "60 miles south of Sheboygan",
      address: "5300 S Howell Ave, Milwaukee, WI 53207",
      note: "Sheboygan's primary international airport. About 1 hour south via I-43. Direct international flights to Mexico, Caribbean, and Canada. Passport required for all international flights.",
      flightUrl: "https://www.mitchellairport.com/",
      flightLabel: "MKE Airport",
      featured: true,
    },
    {
      code: "GRB",
      name: "Green Bay Austin Straubel International Airport",
      distance: "65 miles north of Sheboygan",
      address: "2077 Airport Dr, Green Bay, WI 54313",
      note: "Alternative airport for northern Sheboygan County travelers. About 1 hour 10 minutes north via I-43. Connections to major hubs via Delta, United, and American.",
      flightUrl: "https://www.flygrb.com/",
      flightLabel: "GRB Airport",
      featured: false,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "130 miles south of Sheboygan",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Major international hub with extensive global connections. About 2 hours 15 minutes south via I-43 and I-94. Near the Chicago Passport Agency.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "O'Hare Airport",
      featured: false,
    },
  ],
};